<template>
  <div>
    <vs-heading v-if="props.title" class="mb-4" type="subhead">{{ props.title }} </vs-heading>

    <v-btn
      v-if="!props.formData && !disabled"
      v-ripple="false"
      class="ma-0 add-attachment attachment-area"
      icon
      plain
      :disabled="data.uploading"
      data-test="file-input"
      @click="clickAddAttachment"
    >
      <v-icon large>add</v-icon>
    </v-btn>

    <template v-else>
      <div>
        <div v-if="isImage" class="image-wrapper">
          <a :href="props.formData" @click.prevent>
            <img class="image-attachment" :src="props.formData" @mouseup="imageClicked" />
          </a>
        </div>

        <v-btn
          v-else
          v-ripple="false"
          class="ma-0 attachment-area file-attachment"
          icon
          plain
          :disabled="disabled"
          @mouseup="documentClicked"
        >
          <v-icon size="100">insert_drive_file</v-icon>
        </v-btn>

        <v-btn
          color="primary"
          small
          icon
          outlined
          rounded
          class="remove-button"
          :disabled="disabled"
          @click="clickRemoveAttachment"
        >
          <v-icon>close</v-icon>
        </v-btn>

        <vs-text class="file-name">{{ fileName }}</vs-text>
      </div>
    </template>

    <input
      ref="uploader"
      type="file"
      class="d-none"
      :accept="fileExtensionRestrictions()"
      @change="onFilesSelected"
    />

    <transition-group name="invalid">
      <div v-for="(message, index) in errorMessages" :key="index + message" class="error-message">
        {{ message }}
      </div>
    </transition-group>

    <div v-if="props.description" class="hint mt-2">
      <vs-text v-if="description" type="subdued">{{ props.description }} </vs-text>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  AcceptedFileExtensions,
  AcceptedFileTypes,
  JPEG,
  JPG,
  maxFileSize,
  PNG,
} from '@/utils/constants'
import { last } from 'lodash'
import { parseDataUrlMimeType } from '@/utils/data-url'
import { computed, reactive, ref } from 'vue'

const props = defineProps<{
  errorSchema?: { errors?: string[] | null } | null
  formData?: string | null
  params: never | null
  rootSchema: never
  schema: never
  title: string | null
  description?: string | null
  uiSchema: never
  disabled: boolean
}>()

const data = reactive<{ uploading: boolean }>({ uploading: false })

const errorMessages = computed(() => props.errorSchema?.errors ?? [])

const dataUrlProps = computed(() => {
  if (props.formData) {
    return parseDataUrlMimeType(props.formData)
  }
  return null
})

const fileName = computed(() => {
  if (dataUrlProps.value) {
    return decodeURIComponent(dataUrlProps.value?.parameters?.get('download') ?? 'unknown')
  }

  return decodeURIComponent(last(new URL(props.formData!).pathname.split('/'))!)
})

const isImage = computed<boolean>(
  () =>
    dataUrlProps.value?.type === 'image' ||
    [PNG.extension, JPEG.extension, JPG.extension].some((ext) => urlFileExtension.value === ext),
)

const urlFileExtension = computed(() => new URL(props.formData!).pathname.split('.').pop())

const uploader = ref<HTMLInputElement | null>(null)

const clickAddAttachment = () => {
  uploader.value?.click()
}

const emit = defineEmits<{ (e: 'change', value: string | null) }>()

const clickRemoveAttachment = () => emit('change', null)

const onFilesSelected = (e: InputEvent) => {
  if ((e.target as HTMLInputElement).files.length === 1) {
    const file = (e.target as HTMLInputElement).files![0]
    handleFile(file)
  }
}

const handleFile = (file: File) => {
  data.uploading = true
  const reader = new FileReader()
  reader.onloadend = function () {
    // convert image file to base64 string
    let dataUrl = reader.result
    const index = (dataUrl as string).indexOf(';')

    dataUrl = `${dataUrl.slice(0, index + 1)}download=${encodeURIComponent(
      file.name,
    )}${dataUrl.slice(index)}`

    data.uploading = false
    emit('change', dataUrl)
  }

  reader.readAsDataURL(file)

  uploader.value!.value = ''
}

/// Todo use this to limit file selection
const isFileValid = (file) => {
  if (!AcceptedFileTypes.includes(file.type)) {
    this.unsupportedFileTypeSelected()
    return false
  } else if (this.convertBinaryBytesToMegaBytes(file.size) > maxFileSize) {
    this.maximumFileSizeExceeded()
    return false
  } else {
    return true
  }
}

const fileExtensionRestrictions = () => AcceptedFileExtensions.map((ext) => '.' + ext).join(', ')

const documentClicked = () => {
  const tag = document.createElement('a')
  tag.download = fileName.value
  tag.href = props.formData!
  tag.click()
}

const imageClicked = () => {
  if (dataUrlProps.value) {
    const image = new Image()
    image.src = props.formData!

    const newWindow = window.open('')
    newWindow.document.write(image.outerHTML)
  } else {
    window.open(props.formData!)
  }
}
</script>

<style scoped>
.file-attachment,
.add-attachment {
  border-radius: 12px;
  padding: 100px;
}

.add-attachment {
  border: 2px dotted var(--color-grey--lighter);
}

.file-attachment {
  border: 2px solid var(--color-blue--lighter);
}

.file-attachment .material-icons {
  color: var(--color-blue--lighter);
}

.image-wrapper {
  display: inline-block;
  vertical-align: middle;
}

img.image-attachment {
  border-radius: 12px;
  width: 194px;
  height: 194px;
  object-fit: cover;
}

.remove-button {
  position: relative;
  top: -94px;
  left: -22px;
  background-color: white;
}

.file-name {
  overflow: hidden;
  text-overflow: ellipsis;
  display: -webkit-box;
  -webkit-line-clamp: 2; /* number of lines to show */
  line-clamp: 2;
  -webkit-box-orient: vertical;
  width: 194px;
}

.error-message {
  color: var(--color-red);
  padding: 0 var(--space-base);
  margin-bottom: var(--space-smaller);
}

.error-message:last-of-type {
  margin-bottom: 0;
}
</style>
